/**
 * Shared constants + helpers used across Beacon's extracted views.
 *
 * Kept small and dependency-free. If it needs axios, the module should
 * stay inside the view that owns it — this file is for pure utilities
 * that the App shell, the calendar, the Quick Comms and the Library
 * all rely on.
 */
import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api/beacon`;

/**
 * Dev-only logger — silent in production builds so the console stays
 * clean, still visible while developing/debugging.
 */
export function logDev(...args) {
  if (process.env.NODE_ENV !== "production") {
    console.warn(...args); // eslint-disable-line no-console
  }
}

/**
 * Wrapper around localStorage.setItem/removeItem that swallows exceptions
 * (Safari private mode, storage-quota-exceeded, etc.). Only UI
 * preferences ever go through here — auth tokens live in httpOnly
 * cookies, never in localStorage.
 */
export const safeStorage = {
  set(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (err) {
      logDev(`localStorage.setItem(${key}) failed — ignoring.`, err);
    }
  },
  remove(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (err) {
      logDev(`localStorage.removeItem(${key}) failed — ignoring.`, err);
    }
  },
};

/**
 * Fetch a saved pack and convert the backend pack-detail shape into the
 * PackWorkspace shape (used by Library + Dashboard "open pack" actions).
 */
export async function fetchPackForWorkspace(packId) {
  const r = await axios.get(`${API}/packs/${packId}`);
  const d = r.data;
  return {
    pack_id: d.pack_id,
    plan_id: d.plan_id,
    topic: {
      id: d.topic?.id,
      topic_name: d.topic?.topic_name,
      theme_name: d.topic?.theme_name,
      topic_framing: d.topic?.topic_framing,
      mental_health_related: d.topic?.mental_health_related || false,
      codes_of_practice: d.topic?.codes_of_practice || [],
    },
    toolbox: d.toolbox && { ...d.toolbox, model_id: "stored" },
    poster: d.poster && { ...d.poster, model_id: "stored" },
    email: d.email,
    intranet: d.intranet,
    source_manifest: d.source_manifest,
  };
}

/**
 * Normalise the applied Context Engine profile into the flat fields the
 * plan-editing endpoints accept (replace-theme / custom-topic).
 */
export function profileFields(appliedProfile) {
  return {
    industry: appliedProfile?.industry || null,
    jurisdictions: appliedProfile?.jurisdictions || [],
    free_text_context: appliedProfile?.free_text_context || null,
  };
}

export const MONTH_ORDER = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const JURISDICTIONS = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

export const INDUSTRIES = [
  "Construction",
  "Mining & Resources",
  "Manufacturing",
  "Transport, Postal & Warehousing",
  "Electricity, Gas, Water & Waste Services (Utilities)",
  "Agriculture, Forestry & Fishing",
  "Healthcare & Social Assistance",
  "Retail Trade",
  "Accommodation & Food Services",
  "Education & Training",
  "Public Administration & Safety (incl. Local Council)",
  "Other",
];

// Slug used by the dynamic-plan LLM prompt + the industry-relevance
// badges on the calendar. Human-readable industry name → short code.
export const INDUSTRY_CODES = {
  "Construction": "construction",
  "Mining & Resources": "mining",
  "Manufacturing": "manufacturing",
  "Transport, Postal & Warehousing": "transport",
  "Electricity, Gas, Water & Waste Services (Utilities)": "utilities",
  "Agriculture, Forestry & Fishing": "agriculture",
  "Healthcare & Social Assistance": "healthcare",
  "Retail Trade": "retail",
  "Accommodation & Food Services": "hospitality",
  "Education & Training": "education",
  "Public Administration & Safety (incl. Local Council)": "public-admin",
  "Other": "other",
};

export const FREE_TEXT_PLACEHOLDER =
  "e.g. We're a Tier-2 civil contractor running roadworks across regional Victoria. Workforce mostly subcontract, average age 45+. (Anything about your sub-industry, workforce or current focus that helps Beacon land the tone right.)";

// Pack workspace tabs — controls both the PackWorkspace tab strip and
// the download filenames on each artefact panel.
export const OUTPUT_TABS = [
  { key: "poster", label: "Poster", sub: "A4 monthly-theme PPTX" },
  { key: "toolbox", label: "Toolbox", sub: "3-page PPTX talk pack" },
  { key: "email", label: "Email", sub: "Rich-text / HTML / Markdown" },
  { key: "intranet", label: "Intranet", sub: "HTML / Markdown article" },
];

// Extensions the Quick Comms source uploader will accept. Kept in sync
// with backend/beacon/source_extractor.py's MIME dispatcher.
export const ACCEPTED_FILE_EXT = ".pdf,.docx,.txt";

// One-click phrase library for the "Why this plan?" rationale panel.
// Each chip appends its phrase to the free-text draft — Claude uses
// this context to re-rank the 12 monthly themes for the operator's
// site pattern. Kept deliberately small (7 chips) so the picker stays
// glanceable; expand only when there's demonstrated demand.
export const NUDGE_CHIPS = [
  { label: "Night-shift heavy", phrase: "Night shift is the majority of hours worked here." },
  { label: "Seasonal fatigue Nov-Feb", phrase: "Heavy summer campaign November through February drives fatigue risk." },
  { label: "Recent SWMS non-conformance", phrase: "Recent audit found repeated SWMS non-conformance on isolations." },
  { label: "New starters onboarding", phrase: "High volume of new starters onboarding this year." },
  { label: "Remote & lone workers", phrase: "Significant remote and lone-worker exposure." },
  { label: "Contractor-dense", phrase: "Contractor workforce outnumbers direct employees." },
  { label: "Recent serious incident", phrase: "Recovering from a recent notifiable incident — safety climate needs rebuilding." },
];

// Compact program names used in the calendar chip (kept tiny so the tile
// stays readable). The full name lives in the chip's title attribute.
export const PROGRAM_SHORT_NAMES = {
  "R U OK? Day": "RUOK?",
  "National Safe Work Month": "NSWM",
  "National Road Safety Week": "NRSW",
  "Tradies National Health Month": "TNHM",
  "Mental Health Month": "MHM",
  "Mental Health Week": "MHW",
  "World Mental Health Day": "WMHD",
  "World Suicide Prevention Day": "WSPD",
  "World First Aid Day": "WFAD",
  "World Day for Safety and Health at Work": "ILO Day",
  "Movember": "Movember",
  "Lifeline Stress Down Day": "Stress Down",
  "Daffodil Day": "Daffodil Day",
  "Heart Week": "Heart Week",
  "National Diabetes Week": "Diabetes Wk",
  "Asthma Week": "Asthma Wk",
  "Dry July": "Dry July",
  "World Cancer Day": "World Cancer",
  "National Skin Cancer Action Week": "Skin Cancer",
  "National Walk to Work Day": "Walk to Work",
  "Truckies' Day": "Truckies' Day",
  "White Ribbon Day": "White Ribbon",
};

export function shortenProgramName(name) {
  if (PROGRAM_SHORT_NAMES[name]) return PROGRAM_SHORT_NAMES[name];
  return name.length > 18 ? `${name.slice(0, 15)}…` : name;
}

export async function extractErrorDetail(err) {
  if (!err.response?.data) return err.message;
  const data = err.response.data;
  if (typeof data === "string") return data;
  if (data.detail) {
    return typeof data.detail === "string"
      ? data.detail
      : JSON.stringify(data.detail);
  }
  return err.message;
}

export async function downloadFromPath(downloadPath, filename) {
  // Cross-origin <a download> is ignored by Chrome / Edge / Safari, so
  // fetch the file as a blob and trigger the download from a same-origin
  // blob: URL. This survives strict-cross-origin and popup-blocker rules.
  try {
    const response = await axios.get(`${BACKEND_URL}${downloadPath}`, {
      responseType: "blob",
      // Send the session cookie so the backend can stamp the export with
      // the signed-in user's saved branding (logo + palette).
      withCredentials: true,
    });
    const blob = new Blob([response.data], {
      type:
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || "beacon.pptx";
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoke after the browser has actually started reading the blob.
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  } catch (e) {
    // Final fallback: open in a new tab so the user at least gets the file.
    window.open(`${BACKEND_URL}${downloadPath}`, "_blank", "noopener");
  }
}
